<template>
    <div id="rc-c" class="retirement-calculator-container">
        <PageHeader
            title="Calculadora de Autonomía Financiera"
            subtitle="Herramienta para hacer una previsión de la edad a la que alcanzarás la autonomía financiera, en función de tu ahorro e inversiones."
        />

        <div class="main-content-grid">
            <div class="form-panel card">
                <h3 class="typo-headline-6 section-title">Datos para la Simulación</h3>
                
                <div class="input-section">
                    <h4 class="typo-capsheading-6 input-section-title">Vida</h4>
                    <div class="form-fields">
                        <div class="form-group">
                            <label for="rc-input-age">Edad actual</label>
                            <input type="text" id="rc-input-age" class="form-input" v-model="current_age_str" @input="handleNumericInput('current_age_str')" placeholder="Ej: 30">
                        </div>
                        <div class="form-group">
                            <label for="rc-input-expectancy">Esperanza de vida</label>
                            <input type="text" id="rc-input-expectancy" class="form-input" v-model="life_expectancy_str" @input="handleNumericInput('life_expectancy_str')" placeholder="Ej: 85">
                        </div>
                    </div>
                </div>

                <div class="input-section">
                    <h4 class="typo-capsheading-6 input-section-title">Capital</h4>
                    <div class="form-fields">
                        <div class="form-group">
                            <label for="rc-input-init-capital">Capital inicial</label>
                            <input type="text" id="rc-input-init-capital" class="form-input" v-model="init_capital_str" @input="handleNumericInput('init_capital_str')" placeholder="Ej: 10000">
                        </div>
                        <div class="form-group">
                            <label for="rc-input-annual-earnings">Ingresos netos anuales</label>
                            <input type="text" id="rc-input-annual-earnings" class="form-input" v-model="annual_capital_str" @input="handleNumericInput('annual_capital_str')" placeholder="Ej: 50000">
                        </div>
                        <div class="form-group">
                            <label for="rc-input-annual-spendings">Gastos anuales</label>
                            <input type="text" id="rc-input-annual-spendings" class="form-input" v-model="annual_spendings_str" @input="handleNumericInput('annual_spendings_str')" placeholder="Ej: 30000">
                        </div>
                        <div class="form-group">
                            <label for="rc-input-annual-inflation">Inflación media anual (%)</label>
                            <input type="text" id="rc-input-annual-inflation" class="form-input" v-model="annual_inflation_str" @input="handleNumericInput('annual_inflation_str')" placeholder="Ej: 2">
                        </div>
                    </div>
                </div>

                <div class="input-section">
                    <h4 class="typo-capsheading-6 input-section-title">Inversión</h4>
                    <div class="form-fields">
                        <div class="form-group">
                            <label for="rc-input-initial-investment">Capital inicial inversión</label>
                            <input type="text" id="rc-input-initial-investment" class="form-input" v-model="init_investment_str" @input="handleNumericInput('init_investment_str')" placeholder="Ej: 5000">
                        </div>
                        <div class="form-group">
                            <label for="rc-input-yearly-investment">Inversiones anuales</label>
                            <input type="text" id="rc-input-yearly-investment" class="form-input" v-model="annual_investment_str" @input="handleNumericInput('annual_investment_str')" placeholder="Ej: 10000">
                        </div>
                        <div class="form-group">
                            <label for="rc-input-average-yearly-return">Rentabilidad anual (%)</label>
                            <input type="text" id="rc-input-average-yearly-return" class="form-input" v-model="annual_returns_str" @input="handleNumericInput('annual_returns_str')" placeholder="Ej: 7">
                        </div>
                        <div class="form-group">
                            <label for="rc-input-investment-costs">Costes fijos anuales de inversión</label>
                            <input type="text" id="rc-input-investment-costs" class="form-input" v-model="annual_investment_costs_str" @input="handleNumericInput('annual_investment_costs_str')" placeholder="Ej: 100">
                        </div>
                    </div>
                </div>
                
                <button class="button button-primary button-calculate" @click="createTable">Calcular Autonomía</button>
            </div>

            <div v-if="chart_generated" class="results-panel card">
                <h3 class="typo-headline-6 section-title">Resultados de la Proyección</h3>
                <div class="result-box">
                    <p class="result-label">Independencia Financiera (Total)</p>
                    <p class="result-value result-highlight">{{ intersection_age_processed }}</p>
                    <small class="result-detail">Edad a la que tu capital total (ahorros + inversiones) cubriría tus necesidades.</small>
                </div>
                <div class="result-box">
                    <p class="result-label">Independencia Financiera (Solo Inversiones)</p>
                    <p class="result-value result-highlight">{{ investment_age_processed }}</p>
                    <small class="result-detail">Edad a la que solo tu capital de inversión cubriría tus necesidades.</small>
                </div>
                <div class="result-box">
                    <p class="result-label">Independencia Financiera (Solo Ahorros)</p>
                    <p class="result-value result-highlight">{{ savings_age_processed }}</p>
                    <small class="result-detail">Edad a la que solo tus ahorros (sin inversión) cubrirían tus necesidades.</small>
                </div>
            </div>
        </div>

        <div v-if="chart_generated" class="chart-panel card">
            <h3 class="typo-headline-6 section-title">Proyección Gráfica</h3>
            <div id="rc-chart-container"></div>
        </div>
    </div>
</template>

<script>
import Highcharts from "highcharts";
import { mapGetters } from "vuex";
import PageHeader from "@/components/common/PageHeader.vue";

export default {
    name: 'RetirementCalculator',
    components: {
        PageHeader
    },
    metaInfo() {
        return {
            title: 'Calculadora de autonomía financiera Eurekers',
            meta:[
                { name: 'description', content: "La calculadora de autonomía financiera permite conocer cuánto tiempo y dinero necesitas para alcanzar la autonomía financiera, ofrecida por Eurekers"}
            ]
        }
    },
    data() {
        return {
            current_age_str: '',
            life_expectancy_str: '',
            init_capital_str: '',
            annual_capital_str: '',
            annual_spendings_str: '',
            annual_inflation_str: '',
            init_investment_str: '',
            annual_investment_str: '',
            annual_returns_str: '',
            annual_investment_costs_str: '',

            chart_generated: false,
            intersection_age: -1,
            investment_age: -1,
            savings_age: -1
        };
    },
    computed: {
        ...mapGetters(['is_mobile_device']),

        current_age() { return this._getParsedIntValue(this.current_age_str); },
        life_expectancy() { return this._getParsedIntValue(this.life_expectancy_str); },
        init_capital() { return this._getParsedNumericValue(this.init_capital_str); },
        annual_capital() { return this._getParsedNumericValue(this.annual_capital_str); },
        annual_expenses() { return this._getParsedNumericValue(this.annual_spendings_str); },
        annual_inflation() { return this._getParsedNumericValue(this.annual_inflation_str); },
        init_investment() { return this._getParsedNumericValue(this.init_investment_str); },
        annual_investment() { return this._getParsedNumericValue(this.annual_investment_str); },
        annual_returns() { return this._getParsedNumericValue(this.annual_returns_str); },
        annual_investment_costs() { return this._getParsedNumericValue(this.annual_investment_costs_str); },

        intersection_age_processed() {
            return this.intersection_age >= 0 ? this.intersection_age + " años" : "No alcanzable con datos actuales";
        },
        investment_age_processed() {
            return this.investment_age >= 0 ? this.investment_age + " años" : "No alcanzable con datos actuales";
        },
        savings_age_processed() {
            return this.savings_age >= 0 ? this.savings_age + " años" : "No alcanzable con datos actuales";
        }
    },
    methods: {
        handleNumericInput(fieldName) {
            let value = this[fieldName];
            if (typeof value !== 'string') value = String(value);
            value = value.replace(/[^0-9.,]/g, ''); 
            const parts = value.split(/[.,]/);
            if (parts.length > 2) {
                value = parts[0] + '.' + parts.slice(1).join('');
            } else if (parts.length === 2) {
                value = parts[0] + '.' + parts[1];
            }
            this[fieldName] = value;
        },
        _getParsedNumericValue(value) {
            if (value === null || value === undefined || String(value).trim() === '') return 0;
            const sValue = String(value).replace(',', '.');
            const num = parseFloat(sValue);
            return isNaN(num) ? 0 : num;
        },
        _getParsedIntValue(value) {
            if (value === null || value === undefined || String(value).trim() === '') return 0;
            const sValue = String(value).replace(/[,.]/g, '');
            const num = parseInt(sValue, 10);
            return isNaN(num) ? 0 : num;
        },

        getRetirementNeedsYPlot: function(current_age, life_expectancy, annual_expenses_val) {
            const from_to_years = life_expectancy - current_age;
            if (from_to_years <=0) return [];
            const res = [];

            const getRetirementNeedsForYear = function(year) {
                const years_left = life_expectancy - year;
                const non_inflationary_needs = years_left * annual_expenses_val;
                return non_inflationary_needs > 0 ? non_inflationary_needs : 0;
            }

            for(let i = 0; i < from_to_years; i++) {
                const x = current_age + i;
                res[i] = getRetirementNeedsForYear(x);
            }
            return res;
        },

        getSavingsCapitalYPlot: function(current_age_val, life_expectancy_val, init_capital_val, annual_capital_val, annual_expenses_val, annual_inflation_rate) {
            const from_to_years = life_expectancy_val - current_age_val;
            if (from_to_years <=0) return [];
            const res = [];
            const annual_savings = annual_capital_val - annual_expenses_val;

            const getSavingsOnYear = function(year_offset) {
                const non_inflationary_savings = init_capital_val + year_offset * annual_savings;
                const cumulative_inflation_effect = Math.pow((1 - (annual_inflation_rate / 100.0)), year_offset);
                const inflationary_savings = non_inflationary_savings * cumulative_inflation_effect;
                return inflationary_savings > 0 ? inflationary_savings : 0;
            }

            for(let i = 0; i < from_to_years; i++) {
                res[i] = getSavingsOnYear(i);
            }
            return res;
        },

        setSavingsNeedsIntersection(current_age_val, needs_plot, savings_plot) {
            this.savings_age = -1;
            for(let i = 0; i < savings_plot.length; i++) {
                if(savings_plot[i] >= needs_plot[i]) {
                    this.savings_age = current_age_val + i;
                    return;
                }
            }
        },

        getInvestmentCapitalYPlot: function(current_age_val, life_expectancy_val, init_investment_val, annual_investment_val, annual_returns_val, annual_investment_costs_val, annual_inflation_rate) {
            const from_to_years = life_expectancy_val - current_age_val;
            if (from_to_years <=0) return [];
            const res = [];

            const getInvestmentCapitalOnYear = function(current_processing_age) {
                const p = init_investment_val;
                const a = annual_investment_val - annual_investment_costs_val;
                const i_rate = annual_returns_val / 100.0;
                const n_years = current_processing_age - current_age_val;

                if (n_years < 0) return 0;

                let future_value;
                if (i_rate === 0) {
                     future_value = p + (a * n_years);
                } else {
                    const first_term_val = p * Math.pow(1 + i_rate, n_years);
                    const second_term_val = a * ( (Math.pow(1 + i_rate, n_years) - 1) / i_rate );
                    future_value = first_term_val + second_term_val;
                }
                
                const cumulative_inflation_effect = Math.pow((1 - (annual_inflation_rate / 100.0)), n_years);
                const inflationary_investments = future_value * cumulative_inflation_effect;
                return inflationary_investments > 0 ? inflationary_investments : 0;
            }

            for(let i = 0; i < from_to_years; i++) {
                const x_age = current_age_val + i;
                const num_contender = getInvestmentCapitalOnYear(x_age);
                res[i] = isNaN(num_contender) ? 0 : num_contender;
            }
            return res;
        },

        setInvestmentsNeedsIntersection(current_age_val, needs_plot, investments_plot) {
            this.investment_age = -1;
            for(let i = 0; i < investments_plot.length; i++) {
                if(investments_plot[i] >= needs_plot[i]) {
                    this.investment_age = current_age_val + i;
                    break;
                }
            }
        },

        getSavingsAndInvestmentsYPlot: function(savingsYPlot, investmentsYPlot, needsYPlot, current_age_val) {
            const res = [];
            const plot_length = Math.min(savingsYPlot.length, investmentsYPlot.length);

            for(let i = 0; i < plot_length; i++) {
                res[i] = (savingsYPlot[i] || 0) + (investmentsYPlot[i] || 0);
            }

            this.intersection_age = -1;
            for(let i = 0; i < res.length; i++) {
                if(needsYPlot[i] !== undefined && res[i] >= needsYPlot[i]) {
                    this.intersection_age = current_age_val + i;
                    break;
                }
            }
            return res;
        },

        createTable: function() {
            if (this.current_age <= 0 || this.life_expectancy <= this.current_age || this.annual_expenses <= 0) {
                this.$vueOnToast.pop('warning', 'Datos inválidos', 'Por favor, revise la edad, esperanza de vida y gastos anuales.');
                this.chart_generated = false;
                return;
            }

            const retirement_needs_y_plot = this.getRetirementNeedsYPlot(
                this.current_age, this.life_expectancy, this.annual_expenses
            );

            const current_capital_savings_y_plot = this.getSavingsCapitalYPlot(
                this.current_age, this.life_expectancy, this.init_capital,
                this.annual_capital, this.annual_expenses, this.annual_inflation
            );

            const current_capital_investments_y_plot = this.getInvestmentCapitalYPlot(
                this.current_age, this.life_expectancy, this.init_investment,
                this.annual_investment, this.annual_returns,
                this.annual_investment_costs, this.annual_inflation
            );

            const current_capital_investments_and_savings_y_plot = this.getSavingsAndInvestmentsYPlot(
                current_capital_savings_y_plot, current_capital_investments_y_plot,
                retirement_needs_y_plot, this.current_age
            );

            this.setSavingsNeedsIntersection(
                this.current_age, retirement_needs_y_plot, current_capital_savings_y_plot
            );

            this.setInvestmentsNeedsIntersection(
                this.current_age, retirement_needs_y_plot, current_capital_investments_y_plot
            );
            
            this.chart_generated = true;

            this.$nextTick(() => {
                Highcharts.chart('rc-chart-container', {
                    chart: {
                        backgroundColor: 'var(--color-surface)',
                        style: { fontFamily: "'Open Sans', sans-serif" }
                    },
                    title: { text: '', style: { color: 'var(--color-text-primary)' } },
                    yAxis: {
                        title: { text: 'Capital (€)', style: { color: 'var(--color-text-secondary)' } },
                        labels: { style: { color: 'var(--color-text-secondary)' } },
                        gridLineColor: 'var(--color-outline-variant)'
                    },
                    xAxis: {
                        title: { text: 'Edad', style: { color: 'var(--color-text-secondary)' } },
                        labels: { style: { color: 'var(--color-text-secondary)' } },
                        lineColor: 'var(--color-outline)',
                        tickColor: 'var(--color-outline)',
                        accessibility: { rangeDescription: `Rango: ${this.current_age} a ${this.life_expectancy}` }
                    },
                    legend: {
                        layout: 'horizontal',
                        align: 'center',
                        verticalAlign: 'bottom',
                        itemStyle: { color: 'var(--color-text-primary)' },
                        itemHoverStyle: { color: 'var(--color-primary)' },
                        backgroundColor: 'var(--color-surface-variant, var(--color-surface))',
                        borderColor: 'var(--color-outline)'
                    },
                    plotOptions: {
                        series: {
                            label: { connectorAllowed: false },
                            pointStart: parseInt(this.current_age)
                        }
                    },
                    series: [
                        { name: 'Capital necesario para jubilarse', data: retirement_needs_y_plot, color: 'var(--color-error, #D32F2F)' },
                        { name: 'Capital de inversiones actual', data: current_capital_investments_y_plot, color: 'var(--color-success, #388E3C)' },
                        { name: 'Capital de ahorros actual', data: current_capital_savings_y_plot, color: 'var(--color-info, #1976D2)' },
                        { name: 'Capital de ahorros e inversiones', data: current_capital_investments_and_savings_y_plot, color: 'var(--color-primary, #00ADD9)', lineWidth: 3 }
                    ],
                    tooltip: {
                        backgroundColor: 'var(--color-background-tooltip, rgba(0,0,0,0.8))',
                        borderColor: 'var(--color-outline)',
                        style: { color: 'var(--color-text-on-tooltip, #fff)' },
                        formatter: function () {
                            return `<b>Edad: ${this.x}</b><br/>${this.series.name}: ${Highcharts.numberFormat(this.y, 0, ',', '.')} €`;
                        }
                    },
                    credits: { enabled: false },
                    responsive: {
                        rules: [{
                            condition: { maxWidth: 600 },
                            chartOptions: {
                                legend: { layout: 'horizontal', align: 'center', verticalAlign: 'bottom' }
                            }
                        }]
                    }
                });

                if (this.is_mobile_device) {
                    const resultsElement = this.$el.querySelector('.results-panel') || this.$el.querySelector('.chart-panel');
                    if (resultsElement) {
                        resultsElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                }
            });
        }
    }
}
</script>

<style lang="scss" scoped>
.retirement-calculator-container {
    background-color: var(--color-background);
    color: var(--color-text-primary);
    padding: 20px 5%;
    min-height: 100vh;
    max-width: 1200px;
    margin: 0 auto;
    box-sizing: border-box;
}

.card {
    background-color: var(--color-surface);
    border-radius: 12px;
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.07);
    padding: 25px 30px;
    margin-bottom: 30px;
    border: 1px solid var(--color-outline);
}

.section-title {
    color: var(--color-text-primary);
    margin-top: 0;
    margin-bottom: 20px; 
    padding-bottom: 10px;
    border-bottom: 1px solid var(--color-outline);
    display: block;
}

.input-section {
    margin-bottom: 30px;
    &:last-of-type {
        margin-bottom: 20px;
    }
    .input-section-title {
        color: var(--color-text-secondary);
        font-weight: 500;
        margin-bottom: 15px;
        text-transform: uppercase;
        letter-spacing: 0.05em;
    }
}

.main-content-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
    gap: 30px;
    align-items: start;
}

.form-panel {
}

.form-fields {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 20px;
}

.form-group {
    display: flex;
    flex-direction: column;
    label {
        display: block;
        margin-bottom: 8px;
        font-size: 0.875rem;
        font-weight: 500;
        color: var(--color-text-secondary);
    }
}

.form-input {
    width: 100%;
    padding: 12px 15px;
    border: 1px solid var(--color-outline);
    border-radius: 6px;
    background-color: var(--color-input-background, var(--color-surface));
    color: var(--color-text-primary);
    font-size: 1rem;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
    box-sizing: border-box;

    &:focus {
        outline: none;
        border-color: var(--color-primary);
        box-shadow: 0 0 0 3px var(--color-primary-alpha, rgba(15, 174, 215, 0.25)); 
    }
    &::placeholder {
        color: var(--color-text-placeholder, var(--color-text-inactive));
        opacity: 0.7;
    }
}

.button {
    padding: 12px 25px;
    font-size: 1rem;
    font-weight: 500;
    border-radius: 6px;
    cursor: pointer;
    transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;
    border: 1px solid transparent;
    text-align: center;
    display: block;
    width: 100%;

    &.button-primary {
        background-color: var(--color-highlight);
        color: var(--color-on-primary, #fff);
        border-color: var(--color-highlight);
        &:hover {
            background-color: var(--color-primary-dark, hsl(195, 95%, 35%));
            border-color: var(--color-primary-dark, hsl(195, 95%, 35%));
        }
    }
}
.button-calculate {
    margin-top: 20px; 
}

.results-panel {
}
.result-box {
    background-color: var(--color-active-surface, var(--color-surface-variant));
    padding: 15px 20px;
    border-radius: 8px;
    border: 1px solid var(--color-outline-variant, var(--color-outline));
    margin-bottom: 15px;
    &:last-child {
        margin-bottom: 0;
    }

    .result-label {
        font-size: 0.8125rem;
        font-weight: 600;
        color: var(--color-text-secondary);
        margin-bottom: 5px;
        text-transform: uppercase;
    }
    .result-value {
        font-size: 1.3rem; 
        font-weight: 700;
        color: var(--color-text-primary);
        margin: 0 0 5px 0;
        line-height: 1.2;
        &.result-highlight {
            color: var(--color-primary); 
        }
    }
     .result-detail {
        font-size: 0.75rem;
        color: var(--color-text-secondary);
        line-height: 1.4;
    }
}

.chart-panel {
}
#rc-chart-container {
    height: 450px; 
    width: 100%;
}

.typo-headline-6 { font-size: 1.25rem; font-weight: 500; line-height: 1.4; color: var(--color-text-primary); }
.typo-capsheading-6 {
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.075em;
    color: var(--color-text-secondary);
}

@media (max-width: 960px) { 
    .main-content-grid {
        grid-template-columns: 1fr; 
    }
}

@media (max-width: 600px) { 
    .retirement-calculator-container {
        padding: 15px;
    }
    .card {
        padding: 20px;
    }
    .form-fields {
        grid-template-columns: 1fr; 
    }
    .button {
        padding: 10px 20px;
        font-size: 0.9rem;
    }
    .result-value {
        font-size: 1.2rem;
    }
    #rc-chart-container {
        height: 350px;
    }
}

h3, h4, p, label {
    margin: 0;
    padding: 0;
}
label { margin-bottom: 8px; }
</style>
